import React from 'react';

import { SelectField, useRecordContext, TextField } from 'react-admin';

import {
  Grid, Box, Typography,
} from '@mui/material';

import { get } from 'lodash';

import Answer from '../Answer';

import languages, { Languages } from '../../../../constants/languages';
import roles, { roleChoices } from '../../../../constants/roles';
import conflictFields from '../../../../constants/conflictFields';
import applyChoices from '../../applyChoices';
import { RoleParameters } from '../../questionParameters';

const CONTENT = {
  [languages.SV]: 'Roll',
  [languages.EN]: 'Role',
  [languages.DE]: 'Rolle',
};

const OTHER_LABEL = {
  [languages.SV]: 'Annat',
  [languages.EN]: 'Other',
  [languages.DE]: 'Sonstiges',
};

const ValueField = ({
  language,
  extraParameters,
}: {
  language?: Languages;
  extraParameters?: RoleParameters;
}) => {
  const record = useRecordContext();

  const value = get(record, 'value');
  const sx = get(record, 'sx', {});

  if (!language) return null;

  const choices = applyChoices(
    roleChoices(language),
    extraParameters?.options,
    extraParameters?.choiceLabels,
  );

  return (
    <Grid size={12}>
      <Grid container spacing={4}>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography sx={sx} variant="label">Alternative</Typography>
            <SelectField sx={sx} choices={choices} record={{ value }} source="value.options" emptyText="-" fontWeight="bold" />
          </Box>
        </Grid>
        {value?.options === roles.OTHER && (
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Box display="flex" flexDirection="column" width="100%">
              <Typography sx={sx} variant="label">{extraParameters?.fields?.other?.label ?? OTHER_LABEL[language]}</Typography>
              <TextField sx={sx} record={{ value }} source="value.other" emptyText="-" fontWeight="bold" />
            </Box>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const Role = ({
  source,
  extraParameters,
}: {
  source?: string;
  extraParameters?: RoleParameters;
}) => {
  const record = useRecordContext();

  const language = record?.language as Languages;

  return (
    <Answer
      label={extraParameters?.label ?? CONTENT[language]}
      source={source ?? 'role'}
      datapoint={conflictFields.ROLE}
      identificationInfo={undefined}
      isComplexType
    >
      <ValueField language={language} extraParameters={extraParameters} />
    </Answer>
  );
};

Role.ValueField = ValueField;

Role.Nested = ({
  source,
  extraParameters,
}: {
  source?: string;
  extraParameters?: RoleParameters;
}) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const record = useRecordContext();
  const sx = get(record, 'sx', {});

  const language = record?.language as Languages;

  const value = get(record, source ?? 'role');

  return (
    <>
      <Grid
        size={{
          xs: 12,
          md: 3,
        }}
      >
        <Box display="flex" flexDirection="column">
          <Typography variant="label">{extraParameters?.label ?? CONTENT[language]}</Typography>
          <SelectField
            choices={applyChoices(
              roleChoices(language),
              extraParameters?.options,
              extraParameters?.choiceLabels,
            )}
            source={`${source ?? 'role'}.options`}
            emptyText="-"
            sx={sx}
          />
        </Box>
      </Grid>
      {value?.options === roles.OTHER && (
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Box display="flex" flexDirection="column">
            <Typography variant="label">{extraParameters?.fields?.other?.label ?? OTHER_LABEL[language]}</Typography>
            <TextField source={`${source ?? 'role'}.other`} emptyText="-" sx={sx} />
          </Box>
        </Grid>
      )}
    </>
  );
};

export default Role;
